import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useProfile, useFollowMutation } from './hooks/useProfile';
import { useGetListArticleByFilter } from '../../hooks/useGetListArticleByFilter';
import { useAtom } from 'jotai';
import { profileAtom } from '../../components/Settings/atom';
import ProfileHeader from './components/ProfileHeader';
import ArticleContainer from '../../components/ArticleContainer';
import TopicContainer from '../../components/TopicContainer';
import CommentContainer from '../../components/CommentContainer';
import { TAB_LIST_PROFILE } from '../../constants/profile.constant';
import { fetchTopics } from 'src/apis/article.api';
import ProfileHeaderSkeleton from './components/ProfileHeaderSkeleton';
import GoBackButton from '../../components/GoBackButton';
import SEO from '../../components/SEO';
import { generateSEOMetadata } from '../../utils/seo';

const Profile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile] = useAtom(profileAtom);

  const [currentTopicId, setCurrentTopicId] = useState<number>(null);
  const [topicLabels, setTopicLabels] = useState([]);
  const [type, setType] = useState<string>('');

  const { data: profileData, isLoading } = useProfile(username);
  const followMutation = useFollowMutation();

  const {
    listArticle: articles,
    loading: isLoadingArticles,
    setListArticle,
    setPagination
  } = useGetListArticleByFilter({
    topic: currentTopicId,
    type,
    profileOwnerId: profileData?.id || null,
    limit: 4,
    isShowIgnoring: true
  });

  // Check if the user is viewing their own profile
  const isProfileOwner = useMemo(
    () => profile?.id === profileData?.id,
    [profile?.id, profileData?.id]
  );

  useEffect(() => {
    const fetchTopicLabels = async () => {
      try {
        const result = await fetchTopics();
        setTopicLabels(result);
      } catch (error) {
        console.error('Error fetching topics:', error);
      }
    };
    fetchTopicLabels();
  }, []);

  const handleSelectedTab = (value: string) => {
    setListArticle([]);
    setPagination((prev) => ({ ...prev, page: 1 }));

    // ignore the default tab type myArticle
    setSearchParams(value === '' ? {} : { tab: value });

    setType(value);
    setCurrentTopicId(null);
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    const topicId = searchParams.get('topicId');

    // avoid unnecessary re-renders
    if (topicId) {
      setCurrentTopicId(Number(topicId));
      setType(tab);
      return;
    }

    if (tab !== type) {
      setType(tab);
      setCurrentTopicId(null);
      setListArticle([]);
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
  }, [searchParams, type, setListArticle, setPagination]);

  // Generate SEO metadata for user profile page
  const displayName = profileData?.firstName || profileData?.username || username || 'User';
  const seoMetadata = generateSEOMetadata.userProfile(username || '', displayName);

  return (
    <div>
      <SEO {...seoMetadata} />
      <div className="w-full flex flex-col px-4 md:px-0 relative">
        <div className="pt-12 md:pt-10 z-20 bg-white">
          <GoBackButton
            onClick={() => {
              navigate('/');
              window.scrollTo(0, 0);
            }}
          />
          <div className="w-full mx-auto">
            {profileData && !isLoading ? (
              <ProfileHeader
                user={profileData}
                isOwner={isProfileOwner}
                onFollow={() =>
                  followMutation.mutate({
                    username: username as string,
                    isFollowing: profileData.isFollowing || false
                  })
                }
                isLoadingFollow={followMutation.isPending}
              />
            ) : (
              <ProfileHeaderSkeleton />
            )}
            <div className="sticky top-[68px] md:top-[80px] bg-white z-20 mx-[-16px]">
              <div className="flex w-full max-w-[604px] mx-auto bg-white overflow-x-scroll no-scrollbar">
                {TAB_LIST_PROFILE.slice(0, isProfileOwner ? TAB_LIST_PROFILE.length : 2).map(
                  (item) => (
                    <div
                      key={item.id}
                      className="min-w-[120px] w-full flex flex-col items-center gap-2 cursor-pointer"
                      onClick={() => {
                        handleSelectedTab(item.value);
                      }}
                    >
                      <p
                        className={`text-xs md:text-base  whitespace-nowrap font-[800] ${String(type) === String(item.value) ? 'text-growth-green-500' : 'text-dim-gray'}`}
                      >
                        {item.name}
                      </p>
                      <div
                        className={`h-[2px] w-full ${String(type) === String(item.value) ? 'bg-growth-green' : 'bg-semi-gray'} rounded-[10px'}`}
                      />
                    </div>
                  )
                )}
              </div>
            </div>
            {(type === 'null' || type === null || type === 'myReading') && (
              <div className="sticky top-[94px] md:top-[114px] max-w-[1020px] mx-[-16px] px-4 md:px-0 md:mx-auto bg-white z-20 py-1 pt-3 md:pt-4">
                <TopicContainer
                  topicLabels={topicLabels}
                  currentTopicId={currentTopicId}
                  setCurrentTopicId={setCurrentTopicId}
                  additionalQueryParams={{ tab: type }}
                />
              </div>
            )}
            <div
              className={`mt-8 md:mt-10 ${type === 'myComments' ? 'max-w-[812px]' : 'max-w-[604px]'} mx-auto`}
            >
              {type !== 'myComments' ? (
                <ArticleContainer
                  loading={isLoadingArticles}
                  listArticle={articles || []}
                  isHiddenFeaturedArticles
                  currentTopicId={currentTopicId}
                  type={type}
                  isProfile={isProfileOwner}
                />
              ) : (
                <CommentContainer
                  loading={isLoadingArticles}
                  listComments={articles || []}
                  isHiddenFeaturedArticles
                  type={type}
                  isMyProfile={isProfileOwner}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
